import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel, NovelMetadata } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import MetadataGenerationModal from './MetadataGenerationModal';
import './NovelMetadataEditor.css';

interface NovelMetadataEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type TabType = 'story' | 'characters' | 'settings' | 'ai-context';
type GenerationFieldType = 'settings' | 'themes';

const NovelMetadataEditor: React.FC<NovelMetadataEditorProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId } = useParams<{ novelId: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Metadata state
  const [metadata, setMetadata] = useState<NovelMetadata>({});
  const [genre, setGenre] = useState<string>('');
  
  // Sync status
  const [syncStatus, setSyncStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState<TabType>('story');

  // AI Generation state
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationFieldType, setGenerationFieldType] = useState<GenerationFieldType | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState<any[] | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Enhancement state
  const [enhancingField, setEnhancingField] = useState<string | null>(null);

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  // Update sync status display every 10 seconds
  useEffect(() => {
    if (lastSyncTime && syncStatus === 'saved') {
      const interval = setInterval(() => {
        setLastSyncTime(new Date(lastSyncTime));
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [lastSyncTime, syncStatus]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getNovel(novelId);
      setNovel(data);
      setGenre(data.genre || '');
      setMetadata(data.metadata || {});
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMetadata = async () => {
    if (!novelId) return;

    try {
      setSyncStatus('saving');
      const updated = await NovelService.updateNovelMetadata(novelId, genre, metadata);
      setNovel(updated);
      setSyncStatus('saved');
      setLastSyncTime(new Date());
      setError(null);
    } catch (err) {
      console.error('Error saving metadata:', err);
      setSyncStatus('error');
      setError('Failed to save metadata');
    }
  };

  const handleToggleVisibility = async () => {
    if (!novel || !novelId) return;

    try {
      const updated = await NovelService.updateNovelVisibility(novelId, !novel.is_public);
      setNovel(updated);
    } catch (err) {
      console.error('Error updating visibility:', err);
      setError('Failed to update visibility');
    }
  };

  const getSyncStatusText = () => {
    if (syncStatus === 'saving') {
      return 'Saving...';
    } else if (syncStatus === 'error') {
      return 'Save failed';
    } else if (lastSyncTime) {
      const now = new Date();
      const diffMs = now.getTime() - lastSyncTime.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      
      if (diffSecs < 5) {
        return 'Saved just now';
      } else if (diffSecs < 60) {
        return `Saved ${diffSecs}s ago`;
      } else if (diffSecs < 3600) {
        const mins = Math.floor(diffSecs / 60);
        return `Saved ${mins}m ago`;
      } else {
        const hours = Math.floor(diffSecs / 3600);
        return `Saved ${hours}h ago`;
      }
    }
    return 'Not saved';
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Setting handlers
  const addSetting = () => {
    setMetadata({
      ...metadata,
      settings: [...(metadata.settings || []), '']
    });
  };

  const updateSetting = (index: number, value: string) => {
    const newSettings = [...(metadata.settings || [])];
    newSettings[index] = value;
    setMetadata({ ...metadata, settings: newSettings });
  };

  const removeSetting = (index: number) => {
    const newSettings = [...(metadata.settings || [])];
    newSettings.splice(index, 1);
    setMetadata({ ...metadata, settings: newSettings });
  };

  // Theme handlers
  const addTheme = () => {
    setMetadata({
      ...metadata,
      themes: [...(metadata.themes || []), '']
    });
  };

  const updateTheme = (index: number, value: string) => {
    const newThemes = [...(metadata.themes || [])];
    newThemes[index] = value;
    setMetadata({ ...metadata, themes: newThemes });
  };

  const removeTheme = (index: number) => {
    const newThemes = [...(metadata.themes || [])];
    newThemes.splice(index, 1);
    setMetadata({ ...metadata, themes: newThemes });
  };

  // AI Generation handlers
  const handleOpenGenerationModal = (fieldType: GenerationFieldType) => {
    setGenerationFieldType(fieldType);
    setGeneratedData(null);
    setGenerationError(null);
    setShowGenerationModal(true);
  };

  const handleGenerateFromChapters = async (chapterIds: string[]) => {
    if (!novelId || !generationFieldType) return;

    try {
      setIsGenerating(true);
      setGenerationError(null);
      
      const result = await NovelService.generateMetadataFromChapters(
        novelId,
        chapterIds,
        generationFieldType
      );

      setGeneratedData(result[generationFieldType] || []);
    } catch (err: any) {
      console.error('Error generating metadata:', err);
      setGenerationError(err.message || 'Failed to generate metadata');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptGenerated = (data: any[]) => {
    if (!generationFieldType) return;

    // Update metadata with generated data
    setMetadata({
      ...metadata,
      [generationFieldType]: data
    });

    setShowGenerationModal(false);
    setGeneratedData(null);
    setGenerationFieldType(null);
  };

  const handleEnhanceField = async (fieldType: string, index: number) => {
    if (!novelId) return;

    const enhanceKey = `${fieldType}-${index}`;
    setEnhancingField(enhanceKey);

    try {
      let currentValue: any;
      let actualFieldType = fieldType;

      if (fieldType === 'settings') {
        currentValue = metadata.settings?.[index] || '';
        actualFieldType = 'setting';
      } else if (fieldType === 'themes') {
        currentValue = metadata.themes?.[index] || '';
        actualFieldType = 'theme';
      } else if (fieldType === 'writingStyle') {
        currentValue = metadata.writingStyle || '';
      } else if (fieldType === 'targetAudience') {
        currentValue = metadata.targetAudience || '';
      } else if (fieldType === 'notes') {
        currentValue = metadata.notes || '';
      }

      const enhanced = await NovelService.enhanceMetadataField(
        novelId,
        actualFieldType,
        currentValue
      );

      // Update the metadata with enhanced value
      if (fieldType === 'settings') {
        const newSettings = [...(metadata.settings || [])];
        newSettings[index] = enhanced;
        setMetadata({ ...metadata, settings: newSettings });
      } else if (fieldType === 'themes') {
        const newThemes = [...(metadata.themes || [])];
        newThemes[index] = enhanced;
        setMetadata({ ...metadata, themes: newThemes });
      } else if (fieldType === 'writingStyle') {
        setMetadata({ ...metadata, writingStyle: enhanced });
      } else if (fieldType === 'targetAudience') {
        setMetadata({ ...metadata, targetAudience: enhanced });
      } else if (fieldType === 'notes') {
        setMetadata({ ...metadata, notes: enhanced });
      }

    } catch (err: any) {
      console.error('Error enhancing field:', err);
      setError('Failed to enhance field');
    } finally {
      setEnhancingField(null);
    }
  };

  if (loading) {
    return (
      <div className="novel-metadata-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
          rightActions={
            <button 
              className="metadata-editor-close-btn"
              onClick={handleBack}
            >
              ✕
            </button>
          }
        />
        <div className="novel-metadata-editor-loading">Loading metadata...</div>
      </div>
    );
  }

  if (error || !novel) {
    return (
      <div className="novel-metadata-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
          rightActions={
            <button 
              className="metadata-editor-close-btn"
              onClick={handleBack}
            >
              ✕
            </button>
          }
        />
        <div className="novel-metadata-editor-error">
          {error || 'Novel not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="novel-metadata-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="metadata-editor-header-center">
            <h1 className="page-header-center-title">{novel.title} /metadata</h1>
            <span className={`metadata-sync-status ${syncStatus}`}>
              {syncStatus === 'saving' && '⟳'}
              {syncStatus === 'saved' && '✓'}
              {syncStatus === 'error' && '✕'}
              {' '}
              {getSyncStatusText()}
            </span>
          </div>
        }
        leftActions={
          <button 
            className="metadata-back-btn"
            onClick={handleBack}
          >
            ← Back
          </button>
        }
        rightActions={
          <button 
            className="metadata-save-btn"
            onClick={handleSaveMetadata}
          >
            Save
          </button>
        }
      />

      <div className="novel-metadata-editor-content">
        <div className="metadata-tabs-nav">
          <button
            className={`metadata-tab-btn ${activeTab === 'story' ? 'active' : ''}`}
            onClick={() => setActiveTab('story')}
          >
            Story
          </button>
          <button
            className={`metadata-tab-btn ${activeTab === 'characters' ? 'active' : ''}`}
            onClick={() => setActiveTab('characters')}
          >
            Characters
          </button>
          <button
            className={`metadata-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button
            className={`metadata-tab-btn ${activeTab === 'ai-context' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai-context')}
          >
            AI Context
          </button>
        </div>

        <div className="metadata-tab-content">
          {activeTab === 'story' && (
            <div className="metadata-tab-panel">
              {/* Visibility */}
              <div className="metadata-section">
                <label className="metadata-label">/visibility</label>
                <button
                  className={`metadata-visibility-toggle ${novel?.is_public ? 'public' : 'private'}`}
                  onClick={handleToggleVisibility}
                >
                  {novel?.is_public ? '🌐 Public' : '🔒 Private'}
                </button>
                <p className="metadata-helper-text">
                  {novel?.is_public 
                    ? 'This novel is visible to everyone. Click to make it private.' 
                    : 'This novel is only visible to you. Click to make it public.'}
                </p>
              </div>

              {/* Genre */}
              <div className="metadata-section">
                <label className="metadata-label">/genre</label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="metadata-select"
                >
                  <option value="">Select genre...</option>
                  <option value="Fantasy">Fantasy</option>
                  <option value="Science Fiction">Science Fiction</option>
                  <option value="Mystery">Mystery</option>
                  <option value="Thriller">Thriller</option>
                  <option value="Romance">Romance</option>
                  <option value="Horror">Horror</option>
                  <option value="Historical Fiction">Historical Fiction</option>
                  <option value="Literary Fiction">Literary Fiction</option>
                  <option value="Adventure">Adventure</option>
                  <option value="Young Adult">Young Adult</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Language */}
              <div className="metadata-section">
                <label className="metadata-label">/language</label>
                <select
                  value={metadata.language || ''}
                  onChange={(e) => setMetadata({ ...metadata, language: e.target.value })}
                  className="metadata-select"
                >
                  <option value="">Select language...</option>
                  <option value="English">English</option>
                  <option value="Vietnamese">Vietnamese</option>
                  <option value="Chinese">Chinese</option>
                  <option value="Other">Other</option>
                </select>
                <p className="metadata-helper-text">
                  The language in which the novel will be written. This helps the AI maintain consistent language style.
                </p>
              </div>

              {/* One-Paragraph Summary */}
              <div className="metadata-section">
                <label className="metadata-label">/summary</label>
                <textarea
                  value={metadata.oneParagraphSummary || ''}
                  onChange={(e) => setMetadata({ ...metadata, oneParagraphSummary: e.target.value })}
                  placeholder="One-paragraph summary of the novel (Snowflake method)..."
                  className="metadata-textarea"
                  rows={4}
                />
                <p className="metadata-helper-text">
                  A concise summary that expands on the core idea, providing context and key story elements.
                </p>
              </div>

              {/* Story Plots */}
              <div className="metadata-section">
                <div className="metadata-section-header">
                  <label className="metadata-label">/plots</label>
                </div>
                {(metadata.plots || []).length === 0 ? (
                  <p className="metadata-empty-text">No plots yet</p>
                ) : (
                  <div className="metadata-preview-list">
                    {(metadata.plots || []).slice(0, 3).map((plot, index) => (
                      <div key={index} className="metadata-preview-item">
                        <span className="metadata-preview-label">Plot {index + 1}:</span>
                        <p className="metadata-preview-text">
                          {plot.length > 100 ? plot.substring(0, 100) + '...' : plot || '(Empty)'}
                        </p>
                      </div>
                    ))}
                    {(metadata.plots || []).length > 3 && (
                      <p className="metadata-preview-more">
                        +{(metadata.plots || []).length - 3} more plot{(metadata.plots || []).length - 3 > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}
                <button 
                  className="metadata-view-all-btn"
                  onClick={() => navigate(`/novel/${novelId}/metadata/plots`)}
                >
                  {(metadata.plots || []).length === 0 ? 'Add Plots' : 'View & Edit All Plots'} →
                </button>
              </div>

            </div>
          )}

          {activeTab === 'characters' && (
            <div className="metadata-tab-panel">
              <div className="metadata-section">
                <div className="metadata-section-header">
                  <label className="metadata-label">/characters</label>
                </div>
                {(metadata.characters || []).length === 0 ? (
                  <p className="metadata-empty-text">No characters yet</p>
                ) : (
                  <div className="metadata-preview-list">
                    {(metadata.characters || []).slice(0, 3).map((character, index) => (
                      <div key={index} className="metadata-preview-item">
                        <div className="metadata-preview-character">
                          <span className="metadata-preview-label">
                            {character.name || '(Unnamed)'}
                          </span>
                          <span className={`metadata-preview-role role-${character.role}`}>
                            {character.role}
                          </span>
                        </div>
                        <p className="metadata-preview-text">
                          {character.description.length > 80 
                            ? character.description.substring(0, 80) + '...' 
                            : character.description || '(No description)'}
                        </p>
                      </div>
                    ))}
                    {(metadata.characters || []).length > 3 && (
                      <p className="metadata-preview-more">
                        +{(metadata.characters || []).length - 3} more character{(metadata.characters || []).length - 3 > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}
                <button 
                  className="metadata-view-all-btn"
                  onClick={() => navigate(`/novel/${novelId}/metadata/characters`)}
                >
                  {(metadata.characters || []).length === 0 ? 'Add Characters' : 'View & Edit All Characters'} →
                </button>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="metadata-tab-panel">
              <div className="metadata-section">
                <div className="metadata-section-header">
                  <label className="metadata-label">/settings</label>
                  <div className="metadata-section-actions">
                    <button 
                      className="metadata-generate-btn" 
                      onClick={() => handleOpenGenerationModal('settings')}
                      disabled={!novel?.chapters || novel.chapters.length === 0}
                      title="Generate settings from chapters"
                    >
                      ✨ Generate
                    </button>
                    <button className="metadata-add-button" onClick={addSetting}>+</button>
                  </div>
                </div>
                {(metadata.settings || []).map((setting, index) => (
                  <div key={index} className="metadata-item">
                    <input
                      value={setting}
                      onChange={(e) => updateSetting(index, e.target.value)}
                      placeholder="Setting..."
                      className="metadata-input"
                    />
                    <div className="metadata-item-actions">
                      <button
                        className="metadata-enhance-btn"
                        onClick={() => handleEnhanceField('settings', index)}
                        disabled={!setting || enhancingField === `settings-${index}`}
                        title="Enhance with AI"
                      >
                        {enhancingField === `settings-${index}` ? '⟳' : '✨'}
                      </button>
                      <button
                        className="metadata-remove-button"
                        onClick={() => removeSetting(index)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'ai-context' && (
            <div className="metadata-tab-panel">
              {/* Themes */}
              <div className="metadata-section">
                <div className="metadata-section-header">
                  <label className="metadata-label">/themes</label>
                  <div className="metadata-section-actions">
                    <button 
                      className="metadata-generate-btn" 
                      onClick={() => handleOpenGenerationModal('themes')}
                      disabled={!novel?.chapters || novel.chapters.length === 0}
                      title="Generate themes from chapters"
                    >
                      ✨ Generate
                    </button>
                    <button className="metadata-add-button" onClick={addTheme}>+</button>
                  </div>
                </div>
                {(metadata.themes || []).map((theme, index) => (
                  <div key={index} className="metadata-item">
                    <input
                      value={theme}
                      onChange={(e) => updateTheme(index, e.target.value)}
                      placeholder="Theme..."
                      className="metadata-input"
                    />
                    <div className="metadata-item-actions">
                      <button
                        className="metadata-enhance-btn"
                        onClick={() => handleEnhanceField('themes', index)}
                        disabled={!theme || enhancingField === `themes-${index}`}
                        title="Enhance with AI"
                      >
                        {enhancingField === `themes-${index}` ? '⟳' : '✨'}
                      </button>
                      <button
                        className="metadata-remove-button"
                        onClick={() => removeTheme(index)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Writing Style */}
              <div className="metadata-section">
                <label className="metadata-label">/writing-style</label>
                <textarea
                  value={metadata.writingStyle || ''}
                  onChange={(e) => setMetadata({ ...metadata, writingStyle: e.target.value })}
                  placeholder="Describe the desired writing style..."
                  className="metadata-textarea"
                  rows={3}
                />
              </div>

              {/* Target Audience */}
              <div className="metadata-section">
                <label className="metadata-label">/target-audience</label>
                <input
                  value={metadata.targetAudience || ''}
                  onChange={(e) => setMetadata({ ...metadata, targetAudience: e.target.value })}
                  placeholder="Target audience..."
                  className="metadata-input"
                />
              </div>

              {/* Additional Notes */}
              <div className="metadata-section">
                <label className="metadata-label">/notes</label>
                <textarea
                  value={metadata.notes || ''}
                  onChange={(e) => setMetadata({ ...metadata, notes: e.target.value })}
                  placeholder="Additional context for AI..."
                  className="metadata-textarea"
                  rows={4}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Generation Modal */}
      {generationFieldType && (
        <MetadataGenerationModal
          isOpen={showGenerationModal}
          onClose={() => setShowGenerationModal(false)}
          fieldType={generationFieldType}
          chapters={novel?.chapters || []}
          currentData={metadata[generationFieldType] || []}
          onGenerate={handleGenerateFromChapters}
          onAccept={handleAcceptGenerated}
          generatedData={generatedData}
          isGenerating={isGenerating}
          error={generationError}
        />
      )}
    </div>
  );
};

export default NovelMetadataEditor;

